import { Component, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { tap } from 'rxjs/operators';
import { DialogButtonConfig } from '@mos-core/components/dialog/dialog.component';
import { SlideOverComponent } from '@mos-core/components/slide-over/slide-over.component';
import { SnackBarHelperComponent, TEXT } from '../../../../../helpers';
import { ContactClient, Email, Project } from '../../../../../interfaces';
import { AuthUserModel, UserModel } from '../../../../../models';
import { UserService } from '../../../../../services/user.service';
import { EmailResponse } from '../../common/export.interfaces';
import { ExportTabsService } from '../export-tabs/common/export-tabs.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientHelper } from '../../../../../helpers';

type ccUsersType = UserModel[] | ContactClient[];

@Component({
	selector: 'app-send-email-form',
	templateUrl: './send-email-form.component.html',
})
export class SendEmailFormComponent {
	@Input() public project: Project;
	@Input() public user: AuthUserModel;
	@Input() public endpoint: string;
	@Input() public attachmentKey: string;
	@Input() public filesFolderKey: string;
	@Input() public selectedClient: ContactClient;
	@ViewChild(SlideOverComponent) public sendEmailSlideOver!: SlideOverComponent;

	public email: Email = {};
	public client: ContactClient;
	public users: UserModel[] = [];
	public ccUsers: ccUsersType = [];
	public busy: boolean = false;
	public sendEmailSuccessButtons: DialogButtonConfig[] = [
		{ label: 'Send Email', action: this.send.bind(this), type: 'primary' },
		{ label: 'Close', action: this.close.bind(this), type: 'normal' },
	];

	private destroyRef = inject(DestroyRef);

	constructor(
		private userService: UserService,
		private exportTabsService: ExportTabsService,
		private snack: SnackBarHelperComponent,
		public clientHelper: ClientHelper,
	) {}

	public send(): void {
		this.busy = true;

		this.email = {
			...this.email,
			attachmentKey: this.attachmentKey,
			filesFolderKey: this.filesFolderKey,
			project: this.project.id,
		};

		this.exportTabsService
			.sendEmail(this.filesFolderKey, this.attachmentKey, this.project.id, this.email, this.endpoint)
			.pipe(
				tap((res: EmailResponse) => {
					if (res.status) {
						this.snack.snackSuccess(res.message);
					} else {
						this.snack.snackError(res.message);
					}
				}),
			)
			.subscribe();
		this.busy = false;
		this.close();
	}

	public close(): void {
		this.sendEmailSlideOver.close();
	}

	public open(): void {
		this.userService
			.getUsers({ isActive: true })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(users => {
					this.users = users;
					const modifiedUsers = users.map(user => ({
						...user,
						documentType: 'users',
					}));
					this.ccUsers = [...modifiedUsers, ...this.clientHelper.getClientCompanyContacts(this.project)];
				}),
			)
			.subscribe();
		//set form defaults
		this.client = this.selectedClient || this.project.clients[0];
		const clientName = this.client.contact.name.split(' ');
		const clientFirstName = clientName[0];
		let body = '';
		// only use the default message when using the project_update route / template
		if (this.endpoint === 'project-update') {
			body = `Dear ${clientFirstName},\n\n\n\nThank you,\n${this.user.fullName}\n${this.user.phone ? this.user.phone + '\n' : ''}${this.user.email}`;
		}

		this.email = {
			sentTo: this.client.email,
			ccEmails: [this.user.email] ?? [],
			replyTo: this.user.email,
			subject: `${this.project.reference} | ${this.project.name}`,
			body: body,
			project: this.project.id,
			attachmentKey: this.attachmentKey,
			filesFolderKey: this.filesFolderKey,
		};
		this.sendEmailSlideOver.open();
	}

	// Custom search for email fields, the user items can be a Contact or User
	public customSearch(term: string, user: any) {
		term = term.toLocaleLowerCase();
		let userEmail = user.email ? user.email.toLocaleLowerCase() : '';
		let name: string;
		if (user.name) {
			name = user.name.toLocaleLowerCase();
		} else {
			name = `${user.firstName} ${user.lastName}`.toLocaleLowerCase();
		}
		return name.indexOf(term) > -1 || userEmail.indexOf(term) > -1 || (name + ' - ' + userEmail).indexOf(term) > -1;
	}

	protected readonly textConstants = TEXT;
}
